import { useCallback, useLayoutEffect, useRef } from 'react';
import { Bold, Italic, Underline, List, ListOrdered } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';

/** Structured HTML → plain lines (lists preserve 1./- markers; matches backend export semantics). */
function htmlToPlain(html) {
  if (!html) return '';
  const tpl = document.createElement('template');
  tpl.innerHTML = String(html).trim();
  const root = tpl.content;

  const chunks = [];

  function flushText(txt) {
    const seg = String(txt ?? '').replace(/[^\S\n]+/g, ' ');
    if (!/\S/.test(seg)) return;
    chunks.push(seg);
  }

  /** @param {Node} node */
  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      flushText(node.nodeValue);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = /** @type {Element} */ (node);
    const tg = el.tagName.toLowerCase();

    if (tg === 'script' || tg === 'style') return;

    if (tg === 'br') {
      chunks.push('\n');
      return;
    }

    if (tg === 'ol') {
      let idx = 0;
      for (const child of el.children) {
        if (child.tagName.toLowerCase() !== 'li') continue;
        idx += 1;
        chunks.push('\n', `${idx}. `);
        Array.from(child.childNodes).forEach(walk);
      }
      chunks.push('\n');
      return;
    }

    if (tg === 'ul') {
      for (const child of el.children) {
        if (child.tagName.toLowerCase() !== 'li') continue;
        chunks.push('\n', '- ');
        Array.from(child.childNodes).forEach(walk);
      }
      chunks.push('\n');
      return;
    }

    if (tg === 'pre') {
      chunks.push('\n', (el.textContent || '').replace(/\r\n?/g, '\n'), '\n');
      return;
    }

    const blockEndsLine = tg === 'p' || tg === 'div' || tg === 'section' || tg === 'article'
      || tg === 'blockquote' || tg === 'header' || tg === 'footer' || tg === 'main' || tg === 'aside';
    const isHeading = /^h[1-6]$/.test(tg);

    if (blockEndsLine || isHeading) {
      Array.from(el.childNodes).forEach(walk);
      chunks.push('\n');
      return;
    }

    Array.from(el.childNodes).forEach(walk);
  }

  Array.from(root.childNodes).forEach(walk);

  let out = chunks.join('');
  out = out.replace(/\r\n?/g, '\n');
  out = out.replace(/[^\S\n]+/g, ' ');
  out = out.replace(/\n[ \t]+/g, '\n');
  out = out.replace(/[ \t]+\n/g, '\n');
  out = out.replace(/\n{3,}/g, '\n\n');

  return out.trim();
}

export function LineItemDescriptionDialog({
  open,
  onOpenChange,
  initialHtml,
  initialPlain = '',
  initialTitle = '',
  onSave,
  saving = false,
}) {
  const editorRef = useRef(null);
  // Latest props mirror so the (stable) callback ref reads current values
  // when Radix mounts the contentEditable in its portal.
  const propsRef = useRef({ initialHtml, initialPlain, initialTitle });
  propsRef.current = { initialHtml, initialPlain, initialTitle };

  const pushEditorDomFromProps = useCallback((node) => {
    const { initialHtml: h, initialPlain: p, initialTitle: t } = propsRef.current;
    const html = String(h || '').trim();
    if (html) {
      node.innerHTML = html;
      return;
    }
    const plain = String(p || '').trim();
    if (plain) {
      node.textContent = plain;
      return;
    }
    node.textContent = String(t || '').trim();
  }, []);

  // Callback ref: fires synchronously during commit when the DOM node attaches.
  // This avoids the Radix portal timing bug where useEffect saw editorRef.current === null.
  const setEditorRef = useCallback((node) => {
    editorRef.current = node;
    if (!node) return;
    pushEditorDomFromProps(node);
  }, [pushEditorDomFromProps]);

  useLayoutEffect(() => {
    if (!open || !editorRef.current) return;
    pushEditorDomFromProps(editorRef.current);
  }, [open, initialHtml, initialPlain, initialTitle, pushEditorDomFromProps]);

  // Use the browser's native rich-text command for formatting. document.execCommand
  // is deprecated in spec but is the only cross-browser primitive that handles
  // partial selections, nested blocks and list toggling correctly. The previous
  // homebrew Range.deleteContents + insertNode path silently dropped selected
  // content when the selection was not already inside a recognised block.
  const exec = (command) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    try { document.execCommand(command, false); } catch (_) { /* noop */ }
  };

  const submit = async () => {
    const html = editorRef.current?.innerHTML || '';
    const plain = htmlToPlain(html);
    // Preserve a meaningful row title across description edits. The modal is
    // the description editor, not the title editor; it only auto-derives a
    // title when the row has no real title yet (empty or "Untitled" placeholder).
    const existingTitle = String(initialTitle || '').trim();
    const isPlaceholderTitle = !existingTitle || existingTitle.toLowerCase() === 'untitled';
    const derivedTitle = (plain.split(/\r?\n/).map((x) => x.trim()).find(Boolean) || '').slice(0, 140);
    const title = isPlaceholderTitle ? (derivedTitle || 'Untitled') : existingTitle;
    await onSave?.({
      title,
      description_html: html,
      description_plain: plain,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="line-item-description-modal">
        <DialogHeader>
          <DialogTitle>Edit line item description</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">Content</label>
            <div className="rounded border border-gray-200 bg-white">
              <div className="flex items-center gap-1 border-b border-gray-200 p-1">
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')} data-testid="desc-bold-btn"><Bold className="h-4 w-4" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')} data-testid="desc-italic-btn"><Italic className="h-4 w-4" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')} data-testid="desc-underline-btn"><Underline className="h-4 w-4" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertUnorderedList')} data-testid="desc-ul-btn"><List className="h-4 w-4" /></Button>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('insertOrderedList')} data-testid="desc-ol-btn"><ListOrdered className="h-4 w-4" /></Button>
              </div>
              <div
                ref={setEditorRef}
                contentEditable
                className="min-h-[220px] whitespace-pre-wrap break-words p-3 text-sm outline-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5"
                data-testid="line-item-description-editor"
                suppressContentEditableWarning
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button type="button" onClick={submit} disabled={saving} data-testid="line-item-description-save">
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
